/**
 * Utilitaires pour la recherche d'adresses avec Google Places et Nominatim
 */

/**
 * Service de recherche d'adresses avec fallback
 */
export class AddressSearchService {
  constructor() {
    this.autocompleteService = null
    this.placesService = null
    this.initialized = false
  }

  /**
   * Initialise les services Google Places
   */
  async initialize() {
    if (this.initialized) return

    try {
      await this.waitForGoogleMaps()

      if (window.google && window.google.maps && window.google.maps.places) {
        this.autocompleteService = new google.maps.places.AutocompleteService()
        this.placesService = new google.maps.places.PlacesService(
          document.createElement('div'),
        )
        this.initialized = true
        console.log('✅ Google Places services initialized successfully')
      } else {
        console.warn('Google Places API not available')
      }
    } catch (error) {
      console.warn('Google Maps services initialization failed:', error)
    }
  }

  /**
   * Attend que Google Maps soit prêt
   */
  waitForGoogleMaps() {
    return new Promise((resolve, reject) => {
      const maxWaitTime = 10000 // 10 seconds max wait
      const startTime = Date.now()

      const checkGoogleMaps = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          console.log('✅ Google Maps API ready')
          resolve()
        } else if (Date.now() - startTime > maxWaitTime) {
          console.warn('⚠️ Google Maps API timeout after 10 seconds')
          reject(new Error('Google Maps API timeout'))
        } else {
          setTimeout(checkGoogleMaps, 100)
        }
      }

      checkGoogleMaps()
    })
  }

  /**
   * Recherche des suggestions d'adresses
   * @param {string} query - Texte à rechercher
   * @returns {Promise<Array>} - Suggestions trouvées
   */
  async searchSuggestions(query) {
    if (!query || query.length < 2) {
      return []
    }

    const suggestions = []

    // Try Google Places first
    if (this.autocompleteService) {
      try {
        console.log('🔍 Searching with Google Places for:', query)
        const predictions = await this.getPlacePredictions(query)
        const googleSuggestions = predictions.map(prediction => ({
          mainText:
            prediction.structured_formatting?.main_text ||
            prediction.description,
          secondaryText: prediction.structured_formatting?.secondary_text || '',
          placeId: prediction.place_id,
          description: prediction.description,
          source: 'google',
        }))
        suggestions.push(...googleSuggestions)
        console.log(
          '✅ Google Places suggestions found:',
          googleSuggestions.length,
        )
        return suggestions
      } catch (error) {
        console.warn('⚠️ Google Places search failed:', error.message)
      }
    }

    // Fallback: Try Nominatim (OpenStreetMap)
    try {
      console.log('🔍 Trying Nominatim fallback for:', query)
      const nominatimResults = await this.searchWithNominatim(query)
      const nominatimSuggestions = nominatimResults.map(result => ({
        mainText: result.display_name.split(',')[0],
        secondaryText: result.display_name.split(',').slice(1).join(','),
        placeId: result.place_id,
        description: result.display_name,
        source: 'nominatim',
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
      }))
      suggestions.push(...nominatimSuggestions)
      console.log(
        '✅ Nominatim suggestions found:',
        nominatimSuggestions.length,
      )
      return suggestions
    } catch (error) {
      console.warn('⚠️ Nominatim search failed:', error.message)
      return []
    }
  }

  /**
   * Récupère les prédictions Google Places
   */
  getPlacePredictions(input) {
    return new Promise((resolve, reject) => {
      this.autocompleteService.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: ['bj', 'tg', 'ng'] }, // Bénin + pays voisins
          types: ['geocode', 'establishment'],
        },
        (predictions, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            resolve(predictions)
          } else if (
            status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            resolve([]) // No results is not an error
          } else {
            reject(new Error(`Google Places status: ${status}`))
          }
        },
      )
    })
  }

  /**
   * Recherche avec Nominatim (OpenStreetMap)
   */
  async searchWithNominatim(query) {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=bj&limit=5&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'RapidooCustomerApp/1.0',
        },
      },
    )

    if (!response.ok) {
      throw new Error('Nominatim search failed')
    }

    return response.json()
  }

  /**
   * Récupère les détails d'un lieu
   */
  async getPlaceDetails(placeId) {
    if (!this.placesService) {
      throw new Error('Places service not initialized')
    }

    return new Promise((resolve, reject) => {
      this.placesService.getDetails(
        {
          placeId,
          fields: ['geometry', 'formatted_address', 'name'],
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            resolve(place)
          } else {
            reject(new Error(`Place details status: ${status}`))
          }
        },
      )
    })
  }

  /**
   * Sélectionne une suggestion et retourne les coordonnées
   */
  async selectSuggestion(suggestion) {
    try {
      let position, address

      if (suggestion.source === 'nominatim') {
        // Use Nominatim coordinates directly
        position = {
          lat: suggestion.lat,
          lng: suggestion.lng,
        }
        address = suggestion.description
      } else {
        // Use Google Places
        const place = await this.getPlaceDetails(suggestion.placeId)
        position = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        }
        address = place.formatted_address
      }

      return {
        coordinates: position,
        address: address,
        locationText: suggestion.mainText,
        details: address,
      }
    } catch (error) {
      console.error('Error selecting suggestion:', error)
      throw new Error('Impossible de sélectionner cette position')
    }
  }
}

/**
 * Instance singleton du service de recherche
 */
export const addressSearchService = new AddressSearchService()

/**
 * Fonctions utilitaires pour l'adresse
 */
export async function getAddressWithFallback(position, googleMapUtils) {
  try {
    return await googleMapUtils.getCurrentAddress(position)
  } catch (error) {
    console.warn('Could not get address for location:', error)
    return `Position: ${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`
  }
}
